import { DetailConsumableItem } from '@/app/models/detailsConsumable';
import { FinancialGroup } from '@/app/models/financalGroup';
import { TransferDetailBase } from '@/app/models/transferDetail';
import { useSearchConsumableBookQuery, useUnitConsumableMutation } from '@/app/services/detailsConsumableApi';
import { useFinancialSubGroupQuery } from '@/app/services/financialGroupApi';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import LateralDrawer from '@/components/common/LateralDrawer';
import { useFinancialGroup } from '@/context/FinancialGroup';
import { useFinancialSubGroup } from '@/context/FinancialSubGroup';
import { usePeriod } from '@/context/PeriodContext';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import GenericAutoComplete from '../../common/GenericAutoComplete';
import NewEditTransferCost from '../NewEditTransferCost';
import ConsumableTable from './ConsumableTable';
import NewEditConsumableDetails from './NewEditConsumableDetails';

interface ConsumableProps {
  group: FinancialGroup;
}

function Consumable(props: ConsumableProps) {
  const theme = useTheme();
  const { group } = props;
  const { code } = useParams();
  const { selectedPeriod } = usePeriod();
  const { t } = useTranslation(['consumable', 'common']);

  const { selectedFinancialSubGroup, setSelectedFinancialSubGroup } = useFinancialSubGroup();
  const { data: financialsubGroup } = useFinancialSubGroupQuery({ groupCode: group?.id }, { skip: !group });
  useEffect(() => {
    if (financialsubGroup && financialsubGroup.length > 0) {
      const found = financialsubGroup.find((item) => item.name === group.name) ?? financialsubGroup[0];
      setSelectedFinancialSubGroup(found);
    }
  }, [financialsubGroup, group, setSelectedFinancialSubGroup]);

  const [originConsumable, setOriginConsumable] = useState<DetailConsumableItem | null>(null);
  const [originConsumableSearch, setOriginConsumableSearch] = useState('');

  const [drawerEdit, setDrawerEdit] = useState(false);
  const [drawerTransfer, setDrawerTransfer] = useState(false);
  const [selectConsumable, setSelectConsumable] = useState<number[]>([]);

  const [selectConsumableItem, setSelectedConsumableItem] = useState<DetailConsumableItem>();
  const [selectedItem, setSelectedItem] = useState<TransferDetailBase | undefined>();

  const { data: consumable } = useSearchConsumableBookQuery(
    {
      period: selectedPeriod?.date as string,
      project: code || '',
      subGroup: selectedFinancialSubGroup?.id as number,
      searchTerm: originConsumableSearch || '',
    },
    {
      skip: !selectedPeriod?.date || !selectedFinancialSubGroup,
    },
  );

  const { selectedFinancialGroup } = useFinancialGroup();

  useEffect(() => {
    if (!selectConsumableItem || !selectedFinancialSubGroup || !selectedPeriod) return;

    setSelectedItem({
      id: selectConsumableItem.id,
      originFinancialGroupCode: selectedFinancialGroup?.code as string,
      financialSubGroupId: selectedFinancialSubGroup.id,
      period: selectedPeriod.date,
      complement: selectConsumableItem.complement,
      newDetail: {
        description: selectConsumableItem.description,
        value: selectConsumableItem.value,
        supplier: selectConsumableItem.supplier || '',
      },
    });
  }, [selectedFinancialSubGroup, selectedFinancialGroup, selectConsumableItem, selectedPeriod]);

  const [unit] = useUnitConsumableMutation();

  const handleUnite = async () => {
    await toast.promise(
      unit({
        ids: selectConsumable,
        period: selectedPeriod?.date as string,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('common:union.success') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
    setDrawerEdit(false);
    setSelectConsumable([]);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <Box sx={{ minWidth: theme.spacing(31), display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography component="p" variant="fieldHeader">
              {t('common:search')}
            </Typography>
            <GenericAutoComplete
              value={originConsumable}
              onChange={(value) => {
                setOriginConsumable(value);
              }}
              equalityTest={(option, value) => option?.id === value?.id}
              getOptionLabel={(option) => (option ? `${option.description ?? ''} - ${option.supplier ?? ''}` : '')}
              onSearch={(value) => {
                setOriginConsumableSearch(value);
              }}
              options={[]}
              placeholder={t('common:enter.project.name.or.code') as string}
              sx={{ width: theme.spacing(80) }}
            />
          </Box>
          <Box sx={{ minWidth: theme.spacing(31) }}>
            <Typography variant="fieldHeader">
              {t('common:total.by.group')}
              <Typography>{formatterMonetaryValue(consumable?.totalValue as number)}</Typography>
            </Typography>
          </Box>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <Stack direction="row" alignItems="center">
            <Button
              variant="primary"
              sx={{ width: theme.spacing(20) }}
              onClick={() => handleUnite()}
              disabled={selectConsumable.length === 0}
            >
              {t('common:unite')}
            </Button>
          </Stack>
        </Box>
      </Box>
      <ConsumableTable
        selectConstruction={selectConsumable}
        setSelectConstruction={setSelectConsumable}
        setDrawerEdit={setDrawerEdit}
        setDrawerTransfer={setDrawerTransfer}
        setSelectedConstructionItem={setSelectedConsumableItem}
        selectConstructionItem={selectConsumableItem as DetailConsumableItem}
        data={consumable}
      />
      <LateralDrawer open={drawerEdit} setOpen={setDrawerEdit} position="right" size="bigger">
        <NewEditConsumableDetails
          selectItem={selectConsumableItem as DetailConsumableItem}
          setDrawerOpen={setDrawerEdit}
        />
      </LateralDrawer>
      <LateralDrawer open={drawerTransfer} setOpen={setDrawerTransfer} position="right" size="bigger">
        <NewEditTransferCost selectedItem={selectedItem as TransferDetailBase} setDrawerOpen={setDrawerTransfer} />
      </LateralDrawer>
    </Box>
  );
}

export default Consumable;
